import { useEffect, useId, useRef, type CSSProperties } from 'react';
import { useMovimentoReduzido, usePonteiroFino } from '../../lib/movimento';

/**
 * A Ana como personagem: uma pedrinha clara e macia só com olhos pretos.
 * A expressão toda está nos olhos (forma, direção, piscar).
 * SVG próprio, animado só com CSS (transform e opacidade), sem biblioteca.
 *
 * Estados, como nos assistentes de voz (ouvindo / pensando / falando) e nos
 * mascotes tipo Duo, mas contidos para um app de serviço:
 * - parada: respira e pisca;
 * - ouvindo: a pessoa está escrevendo, inclina e olha para a caixa;
 * - pensando: olha para cima e três bolhas giram sobre a cabeça;
 * - falando: os olhos quicam por um instante quando a resposta chega;
 * - feliz: pulinho e olhos em arco (ação feita);
 * - preocupada: olhos menores e inclinados (erro);
 * - equipe: olha para o lado, uma pessoa da equipe assumiu.
 *
 * Com movimento reduzido fica a expressão de cada estado, sem animação. O
 * desenho é decorativo (aria-hidden): o estado é dito em texto na conversa.
 */
export type EstadoAna = 'parada' | 'ouvindo' | 'pensando' | 'falando' | 'feliz' | 'preocupada' | 'equipe';

export function AnaPersonagem({ estado = 'parada', tamanho = 48, seguirPonteiro = false, className = '' }: {
  estado?: EstadoAna;
  tamanho?: number;
  /** Olhos acompanham o ponteiro (só mouse, só sem movimento reduzido). */
  seguirPonteiro?: boolean;
  className?: string;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const gradiente = `ana-p-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;
  const reduzido = useMovimentoReduzido();
  const fino = usePonteiroFino();

  useEffect(() => {
    const svg = ref.current;
    if (!svg || !seguirPonteiro || reduzido || !fino) return;
    let quadro = 0;
    const mover = (e: PointerEvent) => {
      cancelAnimationFrame(quadro);
      quadro = requestAnimationFrame(() => {
        const r = svg.getBoundingClientRect();
        const dx = e.clientX - (r.left + r.width / 2);
        const dy = e.clientY - (r.top + r.height / 2);
        const d = Math.hypot(dx, dy) || 1;
        const k = Math.min(1, d / 240);
        svg.style.setProperty('--olhar-x', `${((dx / d) * 1.6 * k).toFixed(2)}px`);
        svg.style.setProperty('--olhar-y', `${((dy / d) * 1.3 * k).toFixed(2)}px`);
      });
    };
    window.addEventListener('pointermove', mover, { passive: true });
    return () => { window.removeEventListener('pointermove', mover); cancelAnimationFrame(quadro); };
  }, [seguirPonteiro, reduzido, fino]);

  return (
    <svg
      ref={ref}
      className={`ana-p ${className}`}
      data-estado={estado}
      viewBox="0 0 64 64"
      width={tamanho}
      height={tamanho}
      style={{ '--tam': `${tamanho}px` } as CSSProperties}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <radialGradient id={gradiente} cx="0.38" cy="0.3" r="0.85">
          <stop offset="0" className="ana-p-cor-1" />
          <stop offset="1" className="ana-p-cor-2" />
        </radialGradient>
      </defs>
      <g className="ana-p-pensar">
        <circle cx="24" cy="5" r="2" />
        <circle cx="32" cy="3" r="2.4" />
        <circle cx="40" cy="5" r="2" />
      </g>
      <g className="ana-p-tudo">
        <ellipse className="ana-p-sombra" cx="32" cy="59" rx="16" ry="2.4" />
        <g className="ana-p-corpo-g">
          {/* Pedrinha macia, mais larga que alta. */}
          <path className="ana-p-corpo" d="M32 13c14 0 23 7 23 19.5S45 55 32 55 9 45 9 32.5 18 13 32 13Z" fill={`url(#${gradiente})`} />
          <g className="ana-p-olhos">
            <g className="ana-p-olho ana-p-olho-e"><ellipse cx="25.5" cy="34.5" rx="2.9" ry="4" className="ana-p-iris" /><path d="M22.5 36q3-3.6 6 0" className="ana-p-feliz" /></g>
            <g className="ana-p-olho ana-p-olho-d"><ellipse cx="38.5" cy="34.5" rx="2.9" ry="4" className="ana-p-iris" /><path d="M35.5 36q3-3.6 6 0" className="ana-p-feliz" /></g>
          </g>
        </g>
      </g>
    </svg>
  );
}
